import { jsaService } from './jsaService'
import { apiEndpoints } from '../config/env'
import { apiService } from './apiService'

export interface OfflineJsaData {
  id: string
  jsaData: Record<string, unknown>
  signature: string // base64 signature
  workOrderId?: number
  technicianId: number
  timestamp: number
  status: 'pending' | 'syncing' | 'synced' | 'failed'
  error?: string
  type: 'travel' | 'workOrder'
  technicianName?: string
}

export interface JsaState {
  exists: boolean
  isOffline: boolean
  isSigned: boolean
  lastModified?: number
  pendingSync: boolean
}

export interface JSAResponse {
  success: boolean
  JSAID?: number
  workOrderJSAID?: number
  offline?: boolean
}

class OfflineJsaService {

  /**
   * Save Travel JSA with offline-first strategy
   */
  async saveTravelJsa(jsaData: Record<string, unknown>, signature?: string): Promise<JSAResponse> {
    const technicianId = jsaData.techID_FK as number
    
    try {
      // 1. Save to offline storage immediately (optimistic update)
      const offlineJsaId = await this.saveJsaOffline(jsaData, signature || '', undefined, technicianId)
      
      // 2. Try immediate sync if online
      if (navigator.onLine) {
        try {
          await this.syncJsa(offlineJsaId)
          return { success: true, JSAID: Date.now() } // Mock response
        } catch (error) {
          // JSA is saved offline, will sync later
          return { success: true, JSAID: Date.now(), offline: true }
        }
      }
      
      // Successfully saved offline
      return { success: true, JSAID: Date.now(), offline: true }
      
    } catch (error) {
      throw error
    }
  }

  /**
   * Save Work Order JSA with offline-first strategy
   * This method mimics the API signature used in WorkOrderJSA component
   */
  async saveWorkOrderJsa(jsaData: Record<string, unknown>, signature?: string): Promise<JSAResponse> {
    const technicianId = jsaData.techID_FK as number
    const workOrderId = jsaData.workOrderID_FK as number
    
    try {
      // 1. Save to offline storage immediately (optimistic update)
      const offlineJsaId = await this.saveJsaOffline(jsaData, signature || '', workOrderId, technicianId)
      
      // 2. Try immediate sync if online
      if (navigator.onLine) {
        try {
          await this.syncJsa(offlineJsaId)
          return { success: true, workOrderJSAID: Date.now() } // Mock response
        } catch (error) {
          // JSA is saved offline, will sync later
          return { success: true, workOrderJSAID: Date.now(), offline: true }
        }
      }
      
      // Successfully saved offline
      return { success: true, workOrderJSAID: Date.now(), offline: true }
      
    } catch (error) {
      throw error
    }
  }

  /**
   * Get JSA with offline-first strategy
   */
  async getJsa(technicianId: number, workOrderId?: number, jsaDate?: string): Promise<{
    data: Record<string, unknown> | null
    state: JsaState
  }> {
    
    // 1. Check offline storage first
    const offlineJsa = await this.getOfflineJsa(technicianId, workOrderId, jsaDate)
    
    const state: JsaState = {
      exists: !!offlineJsa,
      isOffline: !navigator.onLine,
      isSigned: !!offlineJsa?.signature,
      lastModified: offlineJsa?.timestamp,
      pendingSync: offlineJsa?.status === 'pending' || offlineJsa?.status === 'failed'
    }
    
    // 2. If online, try to fetch from server
    if (navigator.onLine) {
      try {
        let serverJsa
        if (workOrderId) {
          // For work order JSA, use the workorder endpoint
          serverJsa = await jsaService.getJSAByTechAndDate(technicianId, jsaDate)
        } else {
          // For travel JSA, use the technician endpoint
          serverJsa = await jsaService.getJSAByTechAndDate(technicianId, jsaDate)
        }
        
        if (serverJsa) {
          return {
            data: serverJsa as unknown as Record<string, unknown>,
            state: {
              exists: true,
              isOffline: false,
              isSigned: !!serverJsa.jsaSignature,
              lastModified: new Date(serverJsa.jsaDate).getTime(),
              pendingSync: false
            }
          }
        }
      } catch (error) {
      }
    }
    
    // 3. Return offline data if available
    if (offlineJsa) {
      return {
        data: {
          ...offlineJsa.jsaData,
          jsaSignature: offlineJsa.signature
        },
        state
      }
    }
    
    // 4. No JSA found
    return {
      data: null,
      state: {
        exists: false,
        isOffline: !navigator.onLine,
        isSigned: false,
        pendingSync: false
      }
    }
  }

  /**
   * Check if JSA exists (offline-first)
   */
  async jsaExists(technicianId: number, workOrderId?: number, jsaDate?: string): Promise<boolean> {
    const result = await this.getJsa(technicianId, workOrderId, jsaDate)
    return result.state.exists
  }

  /**
   * Save JSA to offline storage
   */
  private async saveJsaOffline(
    jsaData: Record<string, unknown>, 
    signature: string, 
    workOrderId?: number,
    technicianId?: number
  ): Promise<string> {
    
    const offlineJsa: OfflineJsaData = {
      id: this.generateJsaId(technicianId || jsaData.techID_FK as number, workOrderId),
      jsaData,
      signature,
      workOrderId,
      technicianId: technicianId || jsaData.techID_FK as number,
      timestamp: Date.now(),
      status: 'pending',
      type: workOrderId ? 'workOrder' : 'travel',
      technicianName: jsaData.technicianName as string || 'Unknown'
    }
    
    // Store in localStorage for now (could be moved to IndexedDB later)
    const storageKey = `offline_jsa_${offlineJsa.id}`
    localStorage.setItem(storageKey, JSON.stringify(offlineJsa))
    
    return offlineJsa.id
  }

  /**
   * Get JSA from offline storage
   */
  private async getOfflineJsa(technicianId: number, workOrderId?: number, jsaDate?: string): Promise<OfflineJsaData | null> {
    const jsaId = this.generateJsaId(technicianId, workOrderId, jsaDate)
    const storageKey = `offline_jsa_${jsaId}`
    
    const stored = localStorage.getItem(storageKey)
    if (stored) {
      try {
        return JSON.parse(stored) as OfflineJsaData
      } catch (error) {
        localStorage.removeItem(storageKey)
      }
    }
    
    return null
  }

  /**
   * Sync offline JSA to server
   */
  private async syncJsa(offlineJsaId: string): Promise<void> {
    const storageKey = `offline_jsa_${offlineJsaId}`
    const stored = localStorage.getItem(storageKey)
    
    if (!stored) {
      throw new Error(`Offline JSA not found: ${offlineJsaId}`)
    }
    
    const offlineJsa: OfflineJsaData = JSON.parse(stored)
    
    // Update status to syncing
    offlineJsa.status = 'syncing'
    localStorage.setItem(storageKey, JSON.stringify(offlineJsa))
    
    try {
      // 1. Check if JSA already exists on server
      let jsaExists = false
      if (offlineJsa.workOrderId) {
        try {
          const response = await apiService.get(`${apiEndpoints.jsa}/workorder/${offlineJsa.workOrderId}`, { requireAuth: false })
          jsaExists = !!response
        } catch {
        }
      }

      // 2. Create JSA on server if it doesn't exist
      if (!jsaExists) {
        const jsaPayload = {
          ...offlineJsa.jsaData,
          jsaDate: new Date(offlineJsa.jsaData.jsaDate as string).toISOString(),
          jsaSignature: ''
        }

        try {
          await apiService.post(apiEndpoints.jsa, jsaPayload)
        } catch (error) {
          // Handle 409 Conflict as success (JSA already exists)
          if (error instanceof Error && error.message.includes('409')) {
            jsaExists = true
          } else {
            throw new Error('Failed to save JSA')
          }
        }
      }
      
      // 3. Upload signature if exists
      if (offlineJsa.signature) {
        const signaturePayload = {
          SignatureBase64: offlineJsa.signature,
          TechID_FK: offlineJsa.jsaData.techID_FK,
          WorkOrderID_FK: offlineJsa.workOrderId,
          JSADate: new Date(offlineJsa.jsaData.jsaDate as string).toISOString()
        }

        await apiService.post(`${apiEndpoints.jsa}/signature`, signaturePayload)
      }
      
      // 4. Mark as synced
      offlineJsa.status = 'synced'
      localStorage.setItem(storageKey, JSON.stringify(offlineJsa))
      
      
    } catch (error) {
      // Mark as failed
      offlineJsa.status = 'failed'
      offlineJsa.error = error instanceof Error ? error.message : 'Unknown error'
      localStorage.setItem(storageKey, JSON.stringify(offlineJsa))
      
      throw error
    }
  }

  /**
   * Sync all pending JSAs
   */
  async syncAllPendingJsas(): Promise<void> {
    const pendingJsas = this.getPendingJsas()
    
    for (const jsaId of pendingJsas) {
      try {
        await this.syncJsa(jsaId)
      } catch (error) {
        // Continue with other JSAs
      }
    }
  }

  /**
   * Get all pending JSA IDs
   */
  private getPendingJsas(): string[] {
    const pendingJsas: string[] = []
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key?.startsWith('offline_jsa_')) {
        try {
          const stored = localStorage.getItem(key)
          if (stored) {
            const jsa: OfflineJsaData = JSON.parse(stored)
            if (jsa.status === 'pending' || jsa.status === 'failed') {
              pendingJsas.push(jsa.id)
            }
          }
        } catch (error) {
          }
      }
    }
    
    return pendingJsas
  }

  /**
   * Generate consistent JSA ID for storage
   */
  private generateJsaId(technicianId: number, workOrderId?: number, jsaDate?: string): string {
    const date = jsaDate || new Date().toISOString().split('T')[0]
    if (workOrderId) {
      return `wo_${workOrderId}_${technicianId}_${date}`
    } else {
      return `travel_${technicianId}_${date}`
    }
  }

  /**
   * Get JSA state for UI
   */
  async getJsaState(technicianId?: number, workOrderId?: number, jsaDate?: string): Promise<JsaState> {
    const result = await this.getJsa(technicianId || 0, workOrderId, jsaDate)
    return result.state
  }

  /**
   * Check if there are pending JSAs to sync
   */
  hasPendingJsas(): boolean {
    return this.getPendingJsas().length > 0
  }

  /**
   * Get detailed information about pending JSAs
   */
  getDetailedPendingJsas(): {
    jsaForms: Array<{
      id: string
      type: 'travel' | 'workOrder'
      technicianName?: string
      workOrderNumber?: string
      date: string
      status: 'pending' | 'syncing' | 'synced' | 'failed'
      error?: string
    }>
    failedCount: number
  } {
    const pendingJsaIds = this.getPendingJsas()
    const jsaForms: Array<{
      id: string
      type: 'travel' | 'workOrder'
      technicianName?: string
      workOrderNumber?: string
      date: string
      status: 'pending' | 'syncing' | 'synced' | 'failed'
      error?: string
    }> = []
    
    for (const jsaId of pendingJsaIds) {
      const storageKey = jsaId
      const storedData = localStorage.getItem(storageKey)
      
      if (storedData) {
        const offlineJsa = JSON.parse(storedData) as OfflineJsaData
        
        jsaForms.push({
          id: jsaId,
          type: offlineJsa.type,
          technicianName: offlineJsa.technicianName,
          workOrderNumber: offlineJsa.workOrderId ? `WO-${offlineJsa.workOrderId}` : undefined,
          date: offlineJsa.jsaData.jsaDate as string,
          status: offlineJsa.status || 'pending',
          error: offlineJsa.error
        })
      }
    }
    
    const failedCount = jsaForms.filter(jsa => jsa.status === 'failed').length
    
    return {
      jsaForms,
      failedCount
    }
  }

  /**
   * Get count of pending JSAs by status
   */
  getPendingJsaCounts(): {
    pending: number
    failed: number
    total: number
  } {
    const detailed = this.getDetailedPendingJsas()
    const pending = detailed.jsaForms.filter(j => j.status === 'pending' || j.status === 'syncing').length
    const failed = detailed.failedCount
    
    return {
      pending,
      failed,
      total: pending
    }
  }

  /**
   * Retry a specific failed JSA
   */
  retryFailedJsa(jsaId: string): void {
    const storedData = localStorage.getItem(jsaId)
    if (storedData) {
      const offlineJsa = JSON.parse(storedData) as OfflineJsaData
      if (offlineJsa.status === 'failed') {
        offlineJsa.status = 'pending'
        offlineJsa.error = undefined
        localStorage.setItem(jsaId, JSON.stringify(offlineJsa))
      }
    }
  }

  /**
   * Clear all failed JSAs
   */
  clearFailedJsas(): void {
    const detailed = this.getDetailedPendingJsas()
    const failedJsas = detailed.jsaForms.filter(j => j.status === 'failed')
    
    for (const jsa of failedJsas) {
      localStorage.removeItem(jsa.id)
    }
  }
}

export const offlineJsaService = new OfflineJsaService()